import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../services/supabase";
import useAutoRefresh from "../hooks/useAutoRefresh";

export default function AdminSupportPage() {
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");

  /* =========================
     LOAD ADMIN USER
  ========================= */
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUser(data.user || null);
    });
  }, []);

  /* =========================
     LOAD TICKETS (OPEN + PENDING)
  ========================= */
  const loadTickets = useCallback(async () => {
  const { data } = await supabase
    .from("support_tickets")
    .select(`
      id, subject, status, created_by, assigned_to,
      created_by_profile:profiles!support_tickets_created_by_fkey (
        id,
        full_name
      )
    `)
    .in("status", ["open", "pending"])
    .order("created_at", { ascending: false });

  setTickets(data || []);
}, []);

  /* =========================
     LOAD MESSAGES
  ========================= */
  const loadMessages = useCallback(async (ticketId) => {
  const { data } = await supabase
    .from("messages")
    .select("id, content, sender_id, created_at")
    .eq("ticket_id", ticketId)
    .order("created_at", { ascending: true });

  setMessages(data || []);
}, []);

  /* =========================
     RELOAD GLOBAL (SAFE)
  ========================= */
  const reloadAll = useCallback(async () => {
  await loadTickets();
  if (selectedTicket?.id) {
    await loadMessages(selectedTicket.id);
  }
}, [loadTickets, loadMessages, selectedTicket?.id]);

/* =========================
   AUTO REFRESH (UNIQUE)
========================= */
useAutoRefresh(reloadAll, 10000);

  /* =========================
     INITIAL LOAD + POLLING
  ========================= */
 useEffect(() => {
  reloadAll();
}, [reloadAll]);

  /* =========================
     OPEN TICKET = AUTO ASSIGN
  ========================= */
  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);

    if (!ticket.assigned_to && user?.id) {
      await supabase
        .from("support_tickets")
        .update({
          assigned_to: user.id,
          status: "pending",
        })
        .eq("id", ticket.id);

      setSelectedTicket({
        ...ticket,
        assigned_to: user.id,
        status: "pending",
      });

      reloadAll();
    } else {
      loadMessages(ticket.id);
    }
  };

  /* =========================
     UPDATE STATUS
  ========================= */
  const updateStatus = async (status) => {
    if (!selectedTicket) return;

    await supabase
      .from("support_tickets")
      .update({ status })
      .eq("id", selectedTicket.id);

    if (status === "closed") {
      setSelectedTicket(null);
      reloadAll();
      return;
    }

    setSelectedTicket({
      ...selectedTicket,
      status,
    });

    reloadAll();
  };

  /* =========================
     REALTIME (BONUS)
  ========================= */
  useEffect(() => {
    if (!selectedTicket?.id) return;

    const channel = supabase
      .channel(`admin-ticket-${selectedTicket.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `ticket_id=eq.${selectedTicket.id}`,
        },
        () => loadMessages(selectedTicket.id)
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [selectedTicket?.id, loadMessages]);

  /* =========================
     SEND MESSAGE
  ========================= */
  const sendMessage = async () => {
    if (!newMessage.trim() || !user || !selectedTicket) return;

    await supabase.from("messages").insert({
      sender_id: user.id,
      receiver_id: selectedTicket.created_by,
      ticket_id: selectedTicket.id,
      content: newMessage.trim(),
    });

    setNewMessage("");
    loadMessages(selectedTicket.id);
  };

  /* =========================
     RENDER
  ========================= */
  return (
    <div className="support-layout">
      {/* ================= SIDEBAR ================= */}
      <aside className="support-sidebar">
        <h3>Tickets Support</h3>

        {tickets.length === 0 ? (
          <p>Aucun ticket actif</p>
        ) : (
          tickets.map((t) => (
            <div
              key={t.id}
              className={`ticket-item ${
                selectedTicket?.id === t.id ? "active" : ""
              }`}
              onClick={() => openTicket(t)}
            >
              <strong>{t.subject}</strong>

              <div className={`ticket-status ${t.status}`}>
                {t.status}
              </div>

              <small>Chauffeur : {t.created_by_profile?.full_name || "—"}</small>
            </div>
          ))
        )}
      </aside>

      {/* ================= CHAT ================= */}
      <main className="support-chat">
        {!selectedTicket ? (
          <div className="chat-placeholder">
            Sélectionnez un ticket pour afficher la conversation
          </div>
        ) : (
          <>
            {/* HEADER */}
            <div className="chat-header">
              <div>
                <h3>{selectedTicket.subject}</h3>
                <span className={`ticket-status ${selectedTicket.status}`}>
                  {selectedTicket.status}
                </span>
              </div>

              <div className="chat-actions">
                <button className="btn-current-status" onClick={() => updateStatus("pending")}>
                  En cours
                </button>
                <button className="btn-chat-actions" onClick={() => updateStatus("closed")}>
                  Fermer
                </button>
              </div>
            </div>

            {/* MESSAGES */}
            <div className="chat-messages">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`chat-message ${
                    m.sender_id === selectedTicket.created_by
                      ? "driver"
                      : "admin"
                  }`}
                >
                  <div className="sender">
                    {m.sender_id === selectedTicket.created_by
                      ? "Chauffeur"
                      : "Admin"}
                  </div>

                  <div className="bubble">{m.content}</div>

                  <small>
                    {new Date(m.created_at).toLocaleTimeString()}
                  </small>
                </div>
              ))}
            </div>

            {/* INPUT */}
            <div className="chat-input">
              <input
                type="text"
                placeholder="Votre message…"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              />
              <button className="btn-support" onClick={sendMessage}>Envoyer</button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}







